import React from "react";

/**
 * Placeholder card shown in product grids while the catalog is loading.
 *
 * Mirrors ProductCard's box model exactly so the grid doesn't visibly reflow
 * when the real cards swap in. ProductCard renders, inside `.card`
 * (flex-col gap-2):
 *   1. a `w-full aspect-[1]` image block,
 *   2. a `.product-card-text` block: a two-line `.product-card-name`
 *      (3rem — the name always reserves both lines) and a
 *      `.product-card-price` (14px/20px),
 *   3. a BubbleSelector capped to one row — `h-10`.
 * Each element below matches one of those, so the skeleton's height is the
 * settled card's height, not an approximation. Keep this in step with
 * ProductCard: the order and the name's two-line height are the two things a
 * change there is most likely to break.
 */
const ProductCardSkeleton: React.FC = () => {
  return (
    <div className="card animate-pulse" aria-hidden="true">
      {/* 1. Image block */}
      <div className="w-full aspect-[1] bg-gray-200 rounded-lg" />

      {/* 2. Title (two lines) + price block */}
      <div className="product-card-text flex flex-col gap-1">
        <div className="h-5 w-full bg-gray-200 rounded" />
        <div className="h-5 w-2/3 bg-gray-200 rounded" />
        <div className="h-4 w-1/3 bg-gray-200 rounded" />
      </div>

      {/* 3. Colour bubble row (h-10, same as the capped BubbleSelector).
             The swatches are 8x8 like the unselected bubbles. */}
      <div className="w-full h-10 flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gray-200" />
        <div className="w-8 h-8 rounded-full bg-gray-200" />
        <div className="w-8 h-8 rounded-full bg-gray-200 hidden md:block" />
        <div className="w-8 h-8 rounded-full bg-gray-200 hidden md:block" />
      </div>
    </div>
  );
};

/**
 * A set of skeleton cards laid out like the real product list.
 *
 * `variant` must match how the products themselves will be rendered, or the
 * placeholders are the wrong size: the browse grid (`ProductList`) uses the
 * page grid, while the homepage (`ProductList isCarousel`) renders
 * fixed-width cards in a scroll row — a full-width 4-column grid there made
 * the skeletons far wider (and, being square, far taller) than the cards that
 * replaced them.
 */
export const ProductGridSkeleton: React.FC<{
  count?: number;
  variant?: "grid" | "carousel";
}> = ({ count = 8, variant = "grid" }) => {
  const cards = (n: number) =>
    Array.from({ length: n }, (_, i) => <ProductCardSkeleton key={i} />);

  if (variant === "carousel") {
    // Mirrors ProductCarousel: a `pl-2 pr-10 space-x-4` scroll row of
    // `w-32 sm:w-36 md:w-44 lg:w-48` cards at every width. Keep the widths and
    // padding in step with that component — a mismatch reflows the row the
    // moment the real cards land.
    return (
      <div className="relative w-full" data-testid="product-grid-skeleton">
        <div className="flex space-x-4 pl-2 pr-10 overflow-hidden">
          {Array.from({ length: count }, (_, i) => (
            <div key={i} className="shrink-0 w-32 sm:w-36 md:w-44 lg:w-48">
              <ProductCardSkeleton />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-productlist-boxes" data-testid="product-grid-skeleton">
      {cards(count)}
    </div>
  );
};

export default ProductCardSkeleton;
